import isNil from 'lodash-es/isNil';

import type {
  CatalogBadgeAPI,
  MultibuyPriceLevel,
  ProductCommodityGroupAPI,
  ProductOfferAPI,
  WarehouseAttributeAPI,
  WarehouseStockAPI,
} from '~/api/Catalog';
import { company } from '~/company/Company';
import { CompanyName } from '~/company/interface';
import {
  calcProductOfferFields,
  type MixAndMatchInfo,
  parseCatalogBadges,
  parseProperties,
  resolveDisplayPrices,
  resolveMixAndMatch,
} from '~/types/Product';
import type { ProductProperties } from '~/types/Product/interface';
import { maxWarehouseSellable } from '~/utils/catalogWarehouse';
import { formatRating } from '~/utils/formaters';
import {
  getApplicableMultibuyLevel,
  getLowestMultibuyLevel,
  isMultibuyActive,
  normalizeCatalogMultibuyLevels,
  sortMultibuyLevels,
} from '~/utils/multibuy';
import { dropUnreachableMultibuyLevels } from '~/utils/orderLimit';

import type { Product } from './Product';

export class Offer {
  public readonly id: number;
  public readonly maxQuantityPerOrder: number;
  public readonly sku: string;
  public readonly name: string;
  public readonly description: string;
  public readonly metadataTitle: Nullable<string>;
  public readonly metadataDescription: Nullable<string>;
  public readonly ratingAverage: number;
  public readonly ratingMarksCount: number;
  public readonly priceFormatted: string;
  public readonly discountPriceFormatted: string;
  public readonly discountPercentage: string;
  public readonly pricePerUnit: string;
  public readonly previewImageThumb: string;
  public readonly multibuyPriceLevels: MultibuyPriceLevel[];
  public readonly mixAndMatch: Nullable<MixAndMatchInfo>;
  public readonly isBackorderAvailable: boolean;
  public readonly backorderLeadTime: number;
  public readonly properties: ProductProperties;
  public readonly selectableProperties: ProductProperties;
  public readonly images: string[];
  public readonly slug: string;
  public readonly productId: number;
  public readonly categoryId: Nullable<number>;
  public readonly categoryName: Nullable<string>;
  public readonly parentCategoryId: Nullable<number>;
  public readonly sellableWarehouses: Record<string, string>;
  /** Priority-ordered per-warehouse stock; null on legacy responses. */
  public readonly stocks: Nullable<WarehouseStockAPI[]>;
  public readonly warehouseAttributes: WarehouseAttributeAPI[];
  public readonly bundleId: Nullable<number>;
  public readonly bundleQuantity: Nullable<number>;
  public readonly productSlug: Nullable<string>;
  /**
   * Legacy badges derived from properties of type `badge`.
   * Unrelated to {@link catalogBadges} — kept as-is for existing consumers.
   */
  public readonly badges: string[];
  /**
   * Badges from the catalog badge reference book, in display order.
   * Localized and colored by the backend.
   */
  public readonly catalogBadges: CatalogBadgeAPI[];
  public readonly commodityGroup: Nullable<{
    id: number;
    name: ProductCommodityGroupAPI;
  }>;
  /** Customs duty for cross-border goods, in minor currency units (kopecks). */
  public readonly customsFee: Nullable<number>;

  // TODO: make field changes using the method of this class
  public price: number;
  public discountPrice: number;
  public discountAmount: number;
  /**
   * Member discount per unit from the last cart calculate, in pence; already
   * inside `discountPrice`. Unset outside the cart and for non-members.
   */
  public membershipDiscountAmount?: number;
  public sellable: number;

  constructor(offer: ProductOfferAPI, product: Product) {
    const { images, imagesThumb } = parseImages(offer.images);

    const [offerProperties, selectableOfferProperties] = parseProperties(
      offer.properties || [],
    );

    const { price, discountPrice } = resolveDisplayPrices(offer);

    this.id = offer.id || product.id;
    this.sku = offer.code;
    this.name = offer.name;
    this.price = price;
    this.priceFormatted = '0';
    this.discountPrice = discountPrice;
    this.discountAmount =
      discountPrice > 0 ? Math.max(0, this.price - discountPrice) : 0;
    this.mixAndMatch = company.mixAndMatchEnabled
      ? resolveMixAndMatch(offer, discountPrice > 0 ? discountPrice : price)
      : null;
    this.discountPriceFormatted = '0';
    this.discountPercentage = '';
    this.pricePerUnit = '';
    this.sellable =
      offer.sellable ??
      (company.hasNewLogistics
        ? maxWarehouseSellable(offer.sellableWarehouses)
        : 0);
    this.description = offer.description || product.description;
    this.metadataTitle = offer.metadataTitle;
    this.metadataDescription = offer.metadataDescription;
    this.isBackorderAvailable = offer.isBackorderAvailable;
    this.maxQuantityPerOrder = offer.maxQuantityPerOrder ?? 0;
    this.backorderLeadTime = offer.backorderLeadTime;
    // Catalog multibuy levels are used whenever the backend provides them
    // (data-driven, like resolveDisplayPrices); companies without levels keep
    // the legacy single-level fallback built from minBasketDiscount* fields.
    this.multibuyPriceLevels = dropUnreachableMultibuyLevels(
      sortMultibuyLevels(
        normalizeCatalogMultibuyLevels(offer.multibuyPriceLevels) ??
          (company.name === CompanyName.CityDrinks
            ? []
            : offer.minBasketDiscountPrice && offer.minBasketDiscountQuantity
              ? [
                  {
                    id: 1,
                    quantity: offer.minBasketDiscountQuantity,
                    unitPrice: offer.minBasketDiscountPrice,
                  },
                ]
              : null),
      ),
      this.maxQuantityPerOrder,
    );
    this.properties = {
      ...product.properties,
      ...offerProperties,
    };
    this.selectableProperties = selectableOfferProperties;
    this.productId = product.id;
    this.slug = product.slug;
    this.categoryId = product.categoryId || -1;
    this.categoryName = product.categoryName || '';
    this.parentCategoryId = product.parentCategoryId || -1;
    this.sellableWarehouses = offer.sellableWarehouses || {};
    this.stocks = offer.stocks ?? product.stocks ?? null;
    this.warehouseAttributes = offer.warehouseAttributes ?? [];
    this.ratingAverage = formatRating(product.ratingAverage || 0);
    this.ratingMarksCount = product.ratingMarksCount || 0;
    this.bundleId = offer.bundleId;
    this.bundleQuantity = offer.bundleQuantity || null;
    this.productSlug = offer.productSlug || null;
    this.commodityGroup = product.commodityGroup;
    this.customsFee = offer.customs_fee ?? null;

    this.badges = (offer.properties || [])
      .filter(({ type, value }) => type === 'badge' && value !== 'false')
      .map(({ name }) => name);

    // An offer's own badges replace the product's rather than extend them, so a
    // variant can opt out of the parent's badges by being given its own.
    const offerBadges = parseCatalogBadges(offer.badges);

    this.catalogBadges = offerBadges.length
      ? offerBadges
      : product.catalogBadges;

    this.images = images;
    this.previewImageThumb =
      imagesThumb[0] ||
      images[0] ||
      product.previewImageThumb ||
      product.previewImage;

    calcProductOfferFields(this);
  }

  /**
   * Возвращает наивысший применимый уровень multibuy для заданного количества.
   */
  public getApplicableMultibuyLevel(
    quantity: number,
  ): MultibuyPriceLevel | null {
    return getApplicableMultibuyLevel(this.multibuyPriceLevels, quantity);
  }

  /**
   * Возвращает минимальный уровень multibuy (с наименьшим quantity).
   */
  public getLowestMultibuyLevel(): MultibuyPriceLevel | null {
    return getLowestMultibuyLevel(this.multibuyPriceLevels);
  }

  /**
   * Проверяет, активен ли multibuy для заданного количества.
   */
  public isMultibuyActive(quantity: number): boolean {
    return isMultibuyActive(this.multibuyPriceLevels, quantity);
  }

  public static validate(offer: ProductOfferAPI, product: Product) {
    const isBundle =
      'productType' in product && product.productType === 'bundle';
    const isValidDiscount = isBundle
      ? true
      : (offer.discountPrice || 0) < (offer.price || 0);

    const hasValidChoices = !offer.properties?.some(
      (prop) =>
        prop.choices &&
        (!Array.isArray(prop.choices) ||
          prop.choices.some((choice) => choice === null)),
    );

    return (
      Boolean(offer.code) &&
      Boolean(offer.name) &&
      !isNil(offer.price) &&
      offer.price > 0 &&
      (isNil(offer.discountPrice) || offer.discountPrice >= 0) &&
      isValidDiscount &&
      (!offer.multibuyPriceLevels ||
        offer.multibuyPriceLevels.length === 0 ||
        offer.multibuyPriceLevels.every(
          (level) => level.quantity > 0 && level.unitPrice > 0,
        )) &&
      hasValidChoices
    );
  }
}

const parseImages = (images: ProductOfferAPI['images']) => {
  const result = { images: [] as string[], imagesThumb: [] as string[] };

  if (!images) {
    return result;
  }

  return images.reduce((acc, { url, thumb }) => {
    if (url) {
      acc.images.push(url);
    } else if (thumb) {
      acc.imagesThumb.push(thumb);
    }

    return acc;
  }, result);
};
